import React, {memo} from 'react';
import {useTranslation} from 'react-i18next';
import config from '../../service/config';
import {
   TscThemeName,
   TscThemeNames,
   TscStyles,
   ControlButton,
   Avatar,
   TscAvatarSize,
   TscButtonType,
   SlideToggle,
   Spinner
} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import {useTscTheme} from '../../context/themeProvider';
import logging from '../../service/logging';
import {IUserNotificationPreferenceModel} from '../../model/userNotificationPreferenceModel';
import UploadLogoModal from '../modals/UploadLogoModal/UploadLogoModal';
import UserQuotas from '../header/UserQuotas';
import {EntitlementType} from '../../model/userQuotas';
import {useUserQuotaStore} from '../../context/userQuotaStore';
import cssConstants from '../../constants/cssConstants';
import userApi from '../../service/userApi';
import {NotificationEventTypes} from '../../constants/Constants';
import SettingsGroup from './SettingsGroup';
import SettingsToggle from './SettingsToggle';

const SettingsContentWrapper = styled.div`
   flex-direction: column;
   overflow: auto;
   margin: 0 2rem;
   display: grid;
   grid-template-columns: 1fr 1fr;
   grid-template-areas:
      'heading heading'
      'account quota'
      'settings settings';
   gap: 1rem;

   @media screen and (max-width: ${cssConstants.settingsContentBreakpoint}px) {
      margin: 0 .5rem;
      grid-template-areas:
         'heading heading'
         'account account'
         'quota quota'
         'settings settings';
   };
`;

const AccountPane = styled.div<{visible: boolean; themeName: TscThemeNames}>`
    grid-area: account;
    width: 100%;
    line-height: 1.4;
    display: flex;
    flex-direction: column;
    justify-content: end;
`;

const AccountBody = styled.div<{visible: boolean; themeName: TscThemeNames}>`
    display: flex;
    height: 100%;
    margin-bottom: 0.75rem;
    align-items: center;
    color: ${props => props.themeName === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};

   @media screen and (max-width: ${cssConstants.settingsContentBreakpoint}px) {
      padding: 0.75rem 0 0;
   }
`;

const AccountActions = styled.div`
    display: flex;

    > * {
        flex:1 1 50%;
        margin-right: 0.5rem;

        &:last-child {
            margin-right: 0;
        }
    }
`;

const UserProfile = styled.div`
  overflow: hidden;
  margin-left: 0.75rem;
`;

const UserDisplayName = styled.div<{theme: TscThemeNames}>`
  font-weight: 600;
  font-size: 1.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
`;

const UserEmail = styled.div<{theme: TscThemeNames}>`
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
`;

const UserQuotasContainer = styled.div<{theme: TscThemeNames}>`
   grid-area: quota;
`;

const CustomUserQuota = styled(UserQuotas)<{upgradeButtonHidden: boolean}>`
   ${({upgradeButtonHidden}) => upgradeButtonHidden && `
      height: calc(100% - 2rem);
      justify-content: center;
   `}
`;

const SettingsContainer = styled.div`
   grid-area: settings;
   
   label {
      white-space: normal;
   }
`;

const Setting = styled.div`
   margin-top: 1rem;
`;

const Description = styled.span`
   font-style: italic;
   font-size: smaller;
`;

const SettingsPane = styled.div<{theme: TscThemeNames}>`
   box-shadow: 0 3px 6px rgba(0,0,0,.15), 0 0 2px rgba(0,0,0,.1);
   font-family: sans-serif;
   display: flex;
   background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.light};
   border-radius: ${TscStyles.border.radius.xl};
   -webkit-box-align: center;
   align-items: center;
   align-content: flex-end;
   margin: 16px auto;
   padding-bottom: 8px;
   width: 100%;
   max-width: 54rem;
   height: fit-content;

   @media screen and (max-width: 896px) {
      margin-left: 16px;
      margin-right: 16px;
   }

   @media screen and (max-width: ${cssConstants.settingsContentBreakpoint}px) {
      margin:0;
   }
`;

const CustomLogoImageContainer = styled.div<{hidden: boolean}>`
   display: ${props => props.hidden ? 'none' : 'block'};
   width: 20rem;
   border: 1px ${TscStyles.color.ui.dusk.medium} solid;
   height: ${cssConstants.headerHeight};
   background-color: ${cssConstants.headerBackgroundColor};
   margin-bottom: 1rem;
   border-radius: ${TscStyles.border.radius.lg};
   box-shadow: 0px 1px 4px rgba(0,0,0,0.2);
`;

const CustomLogoImage = styled.img`
   height: 2.75rem;
   margin: .25rem auto auto auto;
   display: block;
`;

const SettingsTitleContainer = styled.h2`
   font-weight: 400;
   font-size: 2.25rem;
   line-height: 1.875rem;
   margin: 2rem 0 0.75rem 0;

   @media screen and (max-width: ${cssConstants.settingsContentBreakpoint}px) {
      margin-top: 1rem;
   }
`;

const ContentWrapper = styled.div<{theme: TscThemeNames}>`
   font-family: ${TscStyles.font.family.primary};
   display: flex;
   flex-direction: column;
   height: 100%;
   margin: auto;
   width: 100%;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

export const SettingsViewBase: React.FC = () => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const quotaEntitlementType = useUserQuotaStore(s => s.QuotaEntitlementType);
   const disableProFeatures = !(quotaEntitlementType === EntitlementType.pro || quotaEntitlementType === EntitlementType.unlimited);

   const [firstViewNotificationSetting, setFirstViewNotificationSetting] = React.useState<IUserNotificationPreferenceModel>();
   const [newCommentNotificationSetting, setNewCommentNotificationSetting] = React.useState<IUserNotificationPreferenceModel>();
   const [newCommentReplyNotificationSetting, setNewCommentReplyNotificationSetting] = React.useState<IUserNotificationPreferenceModel>();
   const [collectionMediaAddedNotificationSetting, setCollectionMediaAddedNotificationSetting] = React.useState<IUserNotificationPreferenceModel>();
   const [collectionMediaAddedBatchNotificationSetting, setCollectionMediaAddedBatchNotificationSetting] = React.useState<IUserNotificationPreferenceModel>();

   const [isEmailPreferencesLoading, setIsEmailPreferencesLoading] = React.useState(true);
   const [displayUploadModal, setDisplayUploadModal] = React.useState(false);
   const [enableProductBanner, setEnableProductBanner] = React.useState(config.settingsConfig.enableProductBanner);
   const [enableNewUploadConversations, setEnableNewUploadConversations] = React.useState(config.settingsConfig.enableNewUploadConversations);
   const [enableUserAutoCaptions, setEnableUserAutoCaptions] = React.useState(config.settingsConfig.enableUserAutoCaptions);

   const showLogoModal = (): void => {
      setDisplayUploadModal(true);
   };

   const hideLogoModal = (): void => {
      setDisplayUploadModal(false);
   };

   const toggleProductBanner: React.MouseEventHandler = async () => {
      try {
         setEnableProductBanner(!enableProductBanner);
         await userApi.configureProductBanner(!enableProductBanner);
      } catch {
         setEnableProductBanner(enableProductBanner);
      }
   };

   const toggleUserAutoCaptions: React.MouseEventHandler = async () => {
      try {
         setEnableUserAutoCaptions(!enableUserAutoCaptions);
         await userApi.configureUserAutoCaptions(!enableUserAutoCaptions);
      } catch {
         setEnableUserAutoCaptions(enableUserAutoCaptions);
      }
   };

   const toggleNewUploadConversations: React.MouseEventHandler = async () => {
      try {
         setEnableNewUploadConversations(!enableNewUploadConversations);
         await userApi.configureNewUploadConversations(!enableNewUploadConversations);
      } catch {
         setEnableNewUploadConversations(enableNewUploadConversations);
      }
   };

   const getPreferenceOrDefault = (preferences: IUserNotificationPreferenceModel[], eventType: NotificationEventTypes): IUserNotificationPreferenceModel => {
      const existingPreference = preferences.find(e => e.eventType === eventType);
      return existingPreference || {
         eventType: eventType,
         deliveryType: 'Email',
         enabled: true
      };
   };

   const setupPreferences = async () => {
      try {
         const preferences = await userApi.getNotificationPreferences();
         setFirstViewNotificationSetting(getPreferenceOrDefault(preferences, NotificationEventTypes.firstView));
         setNewCommentNotificationSetting(getPreferenceOrDefault(preferences, NotificationEventTypes.newComment));
         setNewCommentReplyNotificationSetting(getPreferenceOrDefault(preferences, NotificationEventTypes.newCommentReply));
         setCollectionMediaAddedNotificationSetting(getPreferenceOrDefault(preferences, NotificationEventTypes.collectionMediaAddedSingle));
         setCollectionMediaAddedBatchNotificationSetting(getPreferenceOrDefault(preferences, NotificationEventTypes.collectionMediaAddedBatch));
      } catch (e) {
         logging.error('Failed to fetch notification preference', e);
      }

      setIsEmailPreferencesLoading(false);
   };

   const AccountPaneSection = (
      <AccountPane
         visible={true}
         themeName={theme}
      >
         <AccountBody
            visible={true}
            themeName={theme}
         >
            <Avatar
               avatarImageUrl={config.user.AvatarImageUrl}
               userDisplayName={config.user.DisplayName}
               userID={config.user.TechSmithId}
               size={TscAvatarSize.large}
            />
            <UserProfile>
               <UserDisplayName
                  title={config.user.DisplayName}
                  theme={theme}
               >
                  {config.user.DisplayName}
               </UserDisplayName>
               <UserEmail
                  title={config.user.EmailAddress}
                  theme={theme}
               >
                  {config.user.EmailAddress}
               </UserEmail>
            </UserProfile>
         </AccountBody>
         <AccountActions>
            <ControlButton
               testId="my-account-button"
               buttonType={TscButtonType.secondary}
               themeName={theme}
               href={config.environmentData.MyAccountUrl}
               label={t('MyAccount')}
               openLinkInNewTab={true}
            />
            <ControlButton
               testId="sign-out-button"
               buttonType={TscButtonType.secondary}
               themeName={theme}
               href={config.environmentData.SignOutUrl}
               label={t('SignOut')}
               openLinkInNewTab={true}
            />
         </AccountActions>
      </AccountPane>
   );

   React.useEffect(() => {
      void setupPreferences();
   }, []);

   return (
      <SettingsPane
         data-test-id="settings-pane"
         theme={theme}
      >
         <ContentWrapper theme={theme}>
            <SettingsContentWrapper
               className="app-content-wrapper"
               id="start-of-content"
            >
               <SettingsTitleContainer>
                  {t('Settings')}
               </SettingsTitleContainer>
               {AccountPaneSection}
               <UserQuotasContainer>
                  <CustomUserQuota
                     upgradeButtonHidden={quotaEntitlementType !== EntitlementType.free}
                     theme={theme}
                  />
               </UserQuotasContainer>
               <SettingsContainer>
                  <SettingsGroup
                     title={t('settings.customLogoHeader')}
                     showProIcon={true}
                     theme={theme}
                  >
                     <CustomLogoImageContainer hidden={!config.settingsConfig.customSiteLogoUrl}>
                        <CustomLogoImage
                           className="logo"
                           alt={t('sidebar.siteLogoAltText', {applicationFullName: config.applicationFullName})}
                           src={config.settingsConfig.customSiteLogoUrl}
                        />
                     </CustomLogoImageContainer>
                     <ControlButton
                        testId="custom-logo-action-button"
                        buttonType={TscButtonType.secondary}
                        themeName={theme}
                        disabled={disableProFeatures}
                        label={config.settingsConfig.customSiteLogoUrl ? t('uploadCustomLogo.editCustomLogo') : t('uploadCustomLogo.action')}
                        onClick={showLogoModal}
                     />
                  </SettingsGroup>
                  <SettingsGroup
                     title={t('settings.productBannerHeader')}
                     showProIcon={true}
                     theme={theme}
                  >
                     <Setting>
                        <SlideToggle
                           labelText={t('settings.productBannerToggleLabel')}
                           isChecked={enableProductBanner}
                           isDisabled={disableProFeatures}
                           onClick={toggleProductBanner}
                        />
                        <Description>
                           {t('settings.productBannerDescription')}
                        </Description>
                     </Setting>
                     <Setting>
                        <SlideToggle
                           labelText={t('settings.newUploadConversationsToggleLabel')}
                           isChecked={enableNewUploadConversations}
                           isDisabled={disableProFeatures}
                           onClick={toggleNewUploadConversations}
                        />
                        <Description>
                           {t('settings.newUploadConversationsDescription')}
                        </Description>
                     </Setting>
                  </SettingsGroup>
                  {config.featureSwitches.EnableAutoCaptionVideo && (
                     <SettingsGroup
                        title={t('settings.userAutoCaptionsHeader')}
                        showProIcon={true}
                        theme={theme}
                     >
                        <Setting>
                           <SlideToggle
                              labelText={t('settings.userAutoCaptionsToggleLabel')}
                              isChecked={!disableProFeatures && enableUserAutoCaptions}
                              isDisabled={disableProFeatures}
                              onClick={toggleUserAutoCaptions}
                           />
                           <Description>
                              {t('settings.userAutoCaptionsDescription')}
                           </Description>
                        </Setting>
                     </SettingsGroup>
                  )}
                  <SettingsGroup
                     title={t('settings.emailNotificationsHeader')}
                     theme={theme}
                  >
                     {isEmailPreferencesLoading && (
                        <Spinner
                           themeName={theme}
                           testId="email-notifications-loading"
                        />
                     )}
                     {!isEmailPreferencesLoading && (
                        <>
                           <SettingsToggle
                              labelText={t('settings.notificationSettings.mediaFirstView')}
                              userNotificationSettings={[firstViewNotificationSetting]}
                           />
                           <SettingsToggle
                              labelText={t('settings.notificationSettings.newComment')}
                              userNotificationSettings={[newCommentNotificationSetting]}
                           />
                           <SettingsToggle
                              labelText={t('settings.notificationSettings.newCommentReply')}
                              userNotificationSettings={[newCommentReplyNotificationSetting]}
                           />
                           <SettingsToggle
                              labelText={t('settings.notificationSettings.collectionMediaAdded')}
                              userNotificationSettings={[collectionMediaAddedNotificationSetting, collectionMediaAddedBatchNotificationSetting]}
                           />
                        </>
                     )}
                  </SettingsGroup>
               </SettingsContainer>
            </SettingsContentWrapper>
         </ContentWrapper>
         {displayUploadModal && <UploadLogoModal onClose={() => hideLogoModal()} />}
      </SettingsPane>
   );
};

export default memo(SettingsViewBase);
